import { useCallback } from "react";

import { useAppDispatch } from "@/hooks/useReduxHooks";
import { companyAssetsApi } from "@/services/(company)/api.assets";
import { companyTimeAttendanceApi } from "@/services/(company)/api.time-attendance";
import { companyAttendanceRegularizationApi } from "@/services/(company)/attendanceRegularizationApi";
import { companyLeaveRequestApi } from "@/services/(company)/leaveRequestApi";
import { companyUserApi } from "@/services/(company)/userApi";

/**
 * Re-reads every endpoint the Workspace widgets render from.
 *
 * Lives with the widget registry rather than in the grid engine on purpose: the
 * engine knows nothing about which API slices a given dashboard draws on, and a
 * generic canvas that hard-coded five company-specific tag lists would stop being
 * generic. The page owns that knowledge and hands the grid a callback.
 *
 * Invalidating tags rather than calling `refetch()` per query means widgets that
 * are not currently mounted — because the user sized them away, or removed them —
 * do not fetch, and the ones that share a cache key fetch once between them.
 */
export function useWorkspaceRefresh() {
  const dispatch = useAppDispatch();

  return useCallback(async () => {
    dispatch(
      companyUserApi.util.invalidateTags([
        "getHeadCount",
        "getRecentEmployees",
      ]),
    );
    dispatch(
      companyLeaveRequestApi.util.invalidateTags([
        "LeaveRequestStat",
        "getOnLeave",
      ]),
    );
    dispatch(
      companyTimeAttendanceApi.util.invalidateTags([
        "getCompanyTimeAttendanceStats",
        "getTimeAttendanceActivity",
        "getTimeAttendanceOverview",
      ]),
    );
    dispatch(
      companyAttendanceRegularizationApi.util.invalidateTags([
        "getPendingApprovals",
      ]),
    );
    dispatch(companyAssetsApi.util.invalidateTags(["companyGetAssetStats"]));
  }, [dispatch]);
}
